// src/documents/pdfFill.js
//
// Motor genérico para llenar documentos: escribe valores ENCIMA de un PDF
// plantilla en posiciones fijas (los PDF del cliente no traen campos
// rellenables). Cada plantilla define su mapa de campos en su propio
// archivo (ej. spolTemplate.js) — este motor no sabe nada de SPOL/LOR.
//
// Coordenadas: se escriben en el sistema "de arriba hacia abajo" (y = 0
// arriba), igual que el SVG exportado de la plantilla, y se convierten acá
// al sistema de PDF (y = 0 abajo).
//
// Un campo:
//   { key, page, x, y, width, align?: 'left'|'center'|'right', size?, maxWidth?, plain? }
//     x/width = la raya donde va el dato; y = la raya (el texto se apoya encima).
//     maxWidth permite desbordar la raya hacia espacio vacío de la derecha.
//     plain = texto que reemplaza texto fijo de la plantilla (sin cambio de
//     color: todo va en negro, pedido del cliente 2026-10-21).
//     wrapTo = { x, y, width }: línea siguiente donde sigue el texto que no
//     entra en la raya (corta entre palabras, en vez de achicar o poner "…").
//     font = 'timesBold' para imitar texto legal en Times negrita (default Helvetica).
// Un parche (texto fijo de la plantilla que hay que tapar/reemplazar):
//   { page, x, y, width, height }  → rectángulo blanco (y = arriba del rect)
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const DEFAULT_SIZE = 8.5;
const MIN_SIZE = 5.5;
export const BASELINE_OFFSET = 1.6; // el texto queda apenas sobre la raya

// Achica la letra hasta que entre en el ancho; si ni al mínimo entra, corta
// con "…" para no pisar texto de la plantilla.
export const fitText = (font, text, width, size = DEFAULT_SIZE) => {
  let current = size;
  while (current > MIN_SIZE && font.widthOfTextAtSize(text, current) > width) current -= 0.25;
  if (font.widthOfTextAtSize(text, current) <= width) return { text, size: current };
  let cut = text;
  while (cut.length > 1 && font.widthOfTextAtSize(`${cut}…`, current) > width) cut = cut.slice(0, -1);
  return { text: `${cut}…`, size: current };
};

// Las fuentes estándar de PDF (WinAnsi) no tienen todos los caracteres:
// normaliza comillas/guiones comunes y omite lo que no se pueda
// escribir, en vez de que falle todo el documento por un nombre raro.
export const sanitizeText = (font, text) => {
  const normalized = String(text).replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"').replace(/\s+/g, ' ');
  return Array.from(normalized).map(ch => {
    try { font.encodeText(ch); return ch; } catch { return ''; }
  }).join('');
};

// Parte el texto entre palabras: lo que entra en `width` a `size` y el resto.
export const splitToFit = (font, text, width, size = DEFAULT_SIZE) => {
  if (font.widthOfTextAtSize(text, size) <= width) return [text, ''];
  const words = text.split(' ');
  let count = 0;
  while (count < words.length && font.widthOfTextAtSize(words.slice(0, count + 1).join(' '), size) <= width) count += 1;
  if (count === 0) return ['', text];
  return [words.slice(0, count).join(' '), words.slice(count).join(' ')];
};

const TEXT_COLOR = rgb(0, 0, 0);

export const fillPdf = async (templateBytes, { fields = [], patches = [] }, values) => {
  const pdf = await PDFDocument.load(templateBytes);
  const fonts = {
    helvetica: await pdf.embedFont(StandardFonts.Helvetica),
    timesBold: await pdf.embedFont(StandardFonts.TimesRomanBold)
  };
  const pages = pdf.getPages();

  patches.forEach(p => {
    const page = pages[p.page];
    const height = page.getHeight();
    page.drawRectangle({ x: p.x, y: height - p.y - p.height, width: p.width, height: p.height, color: rgb(1, 1, 1) });
  });

  fields.forEach(field => {
    const raw = values[field.key];
    if (raw === undefined || raw === null || String(raw).trim() === '') return;
    const page = pages[field.page];
    const height = page.getHeight();
    const font = fonts[field.font] || fonts.helvetica;
    const available = field.maxWidth || field.width;
    let content = sanitizeText(font, String(raw).trim());
    if (field.wrapTo) {
      const [first, rest] = splitToFit(font, content, available - 2, field.size || DEFAULT_SIZE);
      if (rest) {
        const next = fitText(font, rest, field.wrapTo.width - 2, field.size || DEFAULT_SIZE);
        page.drawText(next.text, { x: field.wrapTo.x + 1, y: height - field.wrapTo.y + BASELINE_OFFSET, size: next.size, font, color: TEXT_COLOR });
        content = first;
      }
      if (!content) return;
    }
    const { text, size } = fitText(font, content, available - 2, field.size || DEFAULT_SIZE);
    const textWidth = font.widthOfTextAtSize(text, size);
    let x = field.x + 1;
    if (field.align === 'center') x = field.x + Math.max(0, (field.width - textWidth) / 2);
    if (field.align === 'right') x = field.x + field.width - textWidth - 1;
    page.drawText(text, { x, y: height - field.y + BASELINE_OFFSET, size, font, color: TEXT_COLOR });
  });

  return pdf.save();
};

// Descarga en el navegador.
export const downloadPdf = (bytes, filename) => {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
